import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { getApiErrorMessage } from '../../lib/apiError';
import workflowService from '../../services/workflow.service';
import portfolioService, { type ProductSummary } from '../../services/portfolio.service';
import catalogService from '../../services/catalog.service';
import clientDashboardService, {
  type ClientDashboardData,
} from '../../services/client.dashboard.service';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import GrowthChart from '../../components/client/GrowthChart';
import DepositsTimeline from '../../components/client/DepositsTimeline';
import PortfolioSummaryCards from '../../components/client/PortfolioSummaryCards';
import OfferingNotice from '../../components/OfferingNotice';
import MaturityForecast from '../../components/client/MaturityForecast';
import EnteringFundCard from '../../components/client/EnteringFundCard';
import RecentActivity from '../../components/client/RecentActivity';
import RfcReminderBanner from '../../components/client/RfcReminderBanner';
import { lacksOwnRfc } from '../../components/client/rfc.helpers';
import AccountSuspendedBanner from '../../components/client/AccountSuspendedBanner';
import SecurityHoldBanner from '../../components/client/SecurityHoldBanner';
import PhoneUnprovenBanner from '../../components/client/PhoneUnprovenBanner';
import PayoutDestinationCard from '../../components/client/PayoutDestinationCard';
import ComplianceUpliftBanner from '../../components/client/ComplianceUpliftBanner';
import OnboardingProgressCard from '../../components/client/OnboardingProgressCard';
import TrustStrip from '../../components/client/TrustStrip';
import ProposalsCard from '../../components/client/ProposalsCard';
import YieldSimulatorWidget from '../../components/YieldSimulatorWidget';
import type { CaseListItem } from '../../types/workflow';
import type { PortfolioSummary } from '../../types/finance';
import { colors, gradients, shadows } from '../../styles/theme';

const ONBOARDING_SLUG = 'onboarding_retail';

/** States that hard-redirect to the wizard from the dashboard. We
 *  only redirect on `information_required` — staff explicitly bounced
 *  the case back with a request for more data, so the client needs
 *  to act NOW; the engagement view would be a distraction. The other
 *  pre-approval states render the engagement layout (welcome +
 *  progress + simulator) so the client sees value before being asked
 *  for more paperwork. */
const HARD_REDIRECT_STATES = new Set([
  'information_required',
]);

/** Pre-approval states that render the engagement dashboard
 *  (progress card + simulator + trust strip). Covers the "in flight"
 *  band — anything where the client is mid-onboarding or waiting on
 *  compliance review. `null` (no case yet) is also treated as
 *  engagement so a fresh login lands here, not on an empty screen. */
const ENGAGEMENT_STATES = new Set([
  'registered',           // freshly created — wizard auto-START fires from there
  'pending_profile',
  'pending_pep_aml',
  'pending_documents',
  'under_review',         // compliance reviewing — show "we're on it" copy
]);

/** Onboarding states that mean the client is fully operational and
 *  the portfolio view should be rendered. */
const PORTFOLIO_READY_STATES = new Set(['approved', 'archived']);

interface OnboardingState {
  case: CaseListItem | null;
  loading: boolean;
  /** Set when the lookup itself failed. Distinct from "no case yet". */
  error: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  registered:           'Not started',
  pending_profile:      'Personal data',
  pending_pep_aml:      'PEP / AML',
  pending_documents:    'Documents',
  under_review:         'In review',
  information_required: 'Action needed',
  approved:             'Approved',
  rejected:             'Rejected',
  restricted:           'On hold',
  archived:             'Archived',
};

const STATE_TONE: Record<string, BadgeTone> = {
  approved:             'success',
  rejected:             'danger',
  restricted:           'warning',
  information_required: 'warning',
  under_review:         'info',
  archived:             'neutral',
};

export default function ClientDashboard(): JSX.Element {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [onboarding, setOnboarding] = useState<OnboardingState>({
    case: null,
    loading: true,
    error: null,
  });
  // Bumping this re-runs the application-status lookup ("Try again").
  const [onboardingReload, setOnboardingReload] = useState(0);
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [dashboardData, setDashboardData] = useState<ClientDashboardData | null>(null);
  // Set only when BOTH portfolio calls fail: a partial failure still renders
  // what did arrive rather than hiding it behind an error.
  const [portfolioError, setPortfolioError] = useState<string | null>(null);
  // Bumping this re-runs the load effect, which is what "Try again" needs.
  const [portfolioReload, setPortfolioReload] = useState(0);
  const [hasGenericRfc, setHasGenericRfc] = useState(false);
  // Guards the auto-redirect against a re-render firing it twice.
  const redirectedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setOnboarding((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const res = await workflowService.listCases({
          workflow_slug: ONBOARDING_SLUG,
          status: ['in_progress', 'awaiting_review', 'completed', 'rejected', 'expired'],
          size: 1,
        });
        if (!cancelled) {
          setOnboarding({ case: res.items[0] ?? null, loading: false, error: null });
        }
      } catch (e: unknown) {
        // A failed lookup used to be recorded as "no case", which showed the
        // "Get started" call-to-action to a client who might be halfway through
        // their application, or already approved. Unknown is not the same as none.
        if (!cancelled) {
          setOnboarding({
            case: null,
            loading: false,
            error: getApiErrorMessage(e, 'We could not load your application status just now.'),
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [onboardingReload]);

  // Hard-redirect to the wizard ONLY when staff bounced the case back
  // asking for more info — that's an explicit "your turn" signal and
  // the engagement view would dilute it. All other pre-approval states
  // render the engagement dashboard so the client sees value while
  // they wait / iterate.
  useEffect(() => {
    if (onboarding.loading || redirectedRef.current) return;
    const slug = onboarding.case?.current_state_slug;
    if (slug != null && HARD_REDIRECT_STATES.has(slug)) {
      redirectedRef.current = true;
      navigate('/client/onboarding', { replace: true });
    }
  }, [onboarding, navigate]);

  const currentSlug = onboarding.case?.current_state_slug ?? null;
  const portfolioReady =
    !onboarding.loading &&
    onboarding.case !== null &&
    PORTFOLIO_READY_STATES.has(onboarding.case.current_state_slug);
  const engagementReady =
    !onboarding.loading &&
    onboarding.error === null &&
    !redirectedRef.current &&
    (currentSlug === null || ENGAGEMENT_STATES.has(currentSlug));

  // Catalog for the pre-approval simulator. Lazy — only fetched once
  // the engagement view is actually going to render so approved
  // clients never pay for it.
  const [catalogProducts, setCatalogProducts] = useState<ProductSummary[] | null>(null);
  useEffect(() => {
    if (!engagementReady || catalogProducts !== null) return;
    let cancelled = false;
    catalogService
      .listCatalog(3)
      .then((items) => { if (!cancelled) setCatalogProducts(items); })
      .catch(() => { if (!cancelled) setCatalogProducts([]); });  // fail-quiet — simulator block self-hides on empty
    return () => { cancelled = true; };
  }, [engagementReady, catalogProducts]);

  // #58 pre-approval fit check: the simulator may only run the suitability
  // pre-check once the client's case captures every engine input — a missing
  // value would fail closed and read as a false "not a fit". null = still
  // loading (widget stays silent); false = show the "complete your profile"
  // prompt; true = run the soft pre-check.
  const [fitProfileReady, setFitProfileReady] = useState<boolean | null>(null);
  useEffect(() => {
    if (!engagementReady) return;
    if (!onboarding.case) { setFitProfileReady(false); return; }
    const caseId = onboarding.case.case_id;
    let cancelled = false;
    (async () => {
      try {
        const caseFull = await workflowService.getCaseFull(caseId);
        if (cancelled) return;
        const has = (k: string): boolean => {
          const v = caseFull.data[k];
          if (typeof v === 'number') return Number.isFinite(v);
          return typeof v === 'string' && v.trim() !== '';
        };
        setFitProfileReady(
          has('monthly_income') && has('net_worth') && has('investment_experience_years'),
        );
      } catch {
        if (!cancelled) setFitProfileReady(null); // unknown — widget stays silent
      }
    })();
    return () => { cancelled = true; };
  }, [engagementReady, onboarding.case]);

  // Load summary + chart/timeline in parallel once onboarding is confirmed.
  useEffect(() => {
    if (!portfolioReady) return;
    let cancelled = false;
    (async () => {
      const [summaryRes, dashRes] = await Promise.allSettled([
        portfolioService.getMySummary(),
        clientDashboardService.fetchAll(),
      ]);
      if (cancelled) return;
      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value);
      if (dashRes.status === 'fulfilled') setDashboardData(dashRes.value);
      // Both rejected branches were unhandled, so any failure left the page on
      // skeletons that read "Loading…" with no timeout and no recourse — on the
      // primary screen of the product (error audit, 2026-09-11). A partial
      // failure still renders whatever did arrive; only a total failure
      // replaces the panels.
      if (summaryRes.status === 'rejected' && dashRes.status === 'rejected') {
        setPortfolioError(getApiErrorMessage(
          summaryRes.reason,
          'We could not load your summary just now.',
        ));
      } else {
        setPortfolioError(null);
      }
    })();
    return () => { cancelled = true; };
  }, [portfolioReady, portfolioReload]);

  // RFC nag banner — runs only once onboarding is complete.
  useEffect(() => {
    if (!portfolioReady || !onboarding.case) return;
    const caseId = onboarding.case.case_id;
    let cancelled = false;
    (async () => {
      try {
        const [placeholders, caseFull] = await Promise.all([
          workflowService.getKycPlaceholders(),
          workflowService.getCaseFull(caseId),
        ]);
        if (cancelled) return;
        setHasGenericRfc(
          caseFull.data.country_residence === 'MX' &&
            lacksOwnRfc(caseFull.data.rfc, placeholders.rfc),
        );
      } catch {
        // Silent — banner just doesn't appear if the check fails.
      }
    })();
    return () => { cancelled = true; };
  }, [portfolioReady, onboarding.case]);

  // Derive currency from summary or fall back to MXN.
  const currency = summary
    ? (summary.upcoming_maturities[0]?.currency_code ?? 'MXN')
    : 'MXN';

  return (
    <>
      <PageHeader
        title="Summary"
        subtitle={
          <span>
            Signed in as <span className="font-mono">{user?.email}</span>
          </span>
        }
      />

      {/* Pre-approval engagement: progress + simulator + trust signals.
          Renders for null, registered, pending_profile, pending_pep_aml,
          pending_documents, and under_review. Rejected and restricted
          cases fall through to the OnboardingCallout banner below. */}
      {onboarding.error && (
        <LoadErrorPanel
          message={onboarding.error}
          onRetry={() => setOnboardingReload((n) => n + 1)}
        />
      )}
      {engagementReady ? (
        <EngagementView
          firstName={user?.first_name}
          currentSlug={currentSlug}
          caseId={onboarding.case?.case_id ?? null}
          catalogProducts={catalogProducts}
          fitProfileReady={fitProfileReady}
        />
      ) : (
        !portfolioReady && <OnboardingCallout state={onboarding} />
      )}

      {/* Persistent RFC nag — soft warning, never blocks. */}
      <RfcReminderBanner show={portfolioReady && hasGenericRfc} />

      {/* Compliance hold (0038) — neutral read-only notice; renders only when
          /auth/me reports suspension_status='suspended'. */}
      <AccountSuspendedBanner />
      <SecurityHoldBanner />
      <PhoneUnprovenBanner />
      <PayoutDestinationCard />
      <EnteringFundCard rows={summary?.entering_fund ?? []} />

      {/* Compliance uplift — fires when a workflow version cutoff is
          approaching (state='warning') or already triggered a
          remediation case (state='remediation_required'). Per
          client_engagement_position.md, no countdown / dark patterns. */}
      <ComplianceUpliftBanner />

      {portfolioReady && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20 }}>

          {/* Proposals from an Account Executive or a campaign (0070). */}
          <ProposalsCard />

          {/* ── Row 1: KPI cards ─────────────────────────────────── */}
          {portfolioError && (
            <LoadErrorPanel
              message={portfolioError}
              onRetry={() => setPortfolioReload((n) => n + 1)}
            />
          )}
          {!portfolioError &&
            (summary ? (
              <PortfolioSummaryCards summary={summary} currency={currency} />
            ) : (
              <SummaryCardsSkeleton />
            ))}

          {/* ── Row 2: Growth chart + Deposits timeline ──────────── */}
          <div
            style={{
              display: 'grid',
              gap: 16,
              gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
            }}
          >
            <div className="min-w-0">
              {dashboardData ? (
                <GrowthChart
                  series={dashboardData.series}
                  currency={dashboardData.currency}
                  currentBalance={dashboardData.currentBalance}
                />
              ) : (
                <PanelSkeleton height={320} />
              )}
            </div>
            <div className="min-w-0">
              {dashboardData ? (
                <DepositsTimeline entries={dashboardData.timeline} />
              ) : (
                <PanelSkeleton height={320} />
              )}
            </div>
          </div>

          {/* ── Row 3: Maturities + Recent activity ──────────────── */}
          <div
            style={{
              display: 'grid',
              gap: 16,
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            }}
          >
            {summary ? (
              <MaturityForecast
                maturities={summary.upcoming_maturities}
                available={summary.term_dates_available !== false}
              />
            ) : (
              <PanelSkeleton height={200} />
            )}
            {summary ? (
              <RecentActivity movements={summary.recent_movements} />
            ) : (
              <PanelSkeleton height={200} />
            )}
          </div>

        </div>
      )}
      <OfferingNotice />
    </>
  );
}

/**
 * Shown in place of a section whose data could not be loaded at all: the
 * portfolio panels, or the application status that decides what this dashboard
 * shows. Offers the one action that can help, instead of a skeleton that never
 * resolves or a call-to-action built on a guess.
 */
function LoadErrorPanel({
  message,
  onRetry,
}: Readonly<{ message: string; onRetry: () => void }>): JSX.Element {
  return (
    <div
      role="alert"
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <p style={{ margin: 0, fontSize: 14, color: colors.ink }}>{message}</p>
      <button
        type="button"
        onClick={onRetry}
        style={{
          border: `1px solid ${colors.border}`,
          background: 'transparent',
          color: colors.ink,
          borderRadius: 10,
          padding: '8px 16px',
          fontSize: 13,
          cursor: 'pointer',
        }}
      >
        Try again
      </button>
    </div>
  );
}

// ─── Skeletons ───────────────────────────────────────────────────────

function PanelSkeleton({ height }: Readonly<{ height: number }>): JSX.Element {
  return (
    <div
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <p style={{ margin: 0, fontSize: 13, color: colors.muted }}>Loading…</p>
    </div>
  );
}

function SummaryCardsSkeleton(): JSX.Element {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16,
      }}
    >
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            height: 100,
          }}
        />
      ))}
    </div>
  );
}

// ─── Pre-approval engagement view ───────────────────────────────────
interface EngagementViewProps {
  firstName: string | undefined;
  currentSlug: string | null;
  caseId: string | null;
  catalogProducts: ProductSummary[] | null;
  fitProfileReady: boolean | null;
}

const EngagementView = ({
  firstName,
  currentSlug,
  caseId,
  catalogProducts,
  fitProfileReady,
}: Readonly<EngagementViewProps>): JSX.Element => {
  // Friendly greeting if we have the name; falls back to a neutral
  // hello if /auth/me hasn't populated it yet (shouldn't normally
  // happen — login.tsx hydrates first_name fire-and-forget).
  const greeting = firstName ? `Welcome, ${firstName}` : 'Welcome';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20 }}>
      {/* Greeting line — intentionally small; the progress card below
          carries the primary visual weight. */}
      <p className="text-sm" style={{ color: colors.muted }}>
        {greeting}. Let&apos;s get your account ready so you can make your first contribution.
      </p>

      <OnboardingProgressCard currentSlug={currentSlug} caseId={caseId} />

      {/* Simulator — self-hides while the catalog is loading and when
          the curated list comes back empty (e.g. finance hasn't
          flagged any products yet). The widget is pure client-side,
          so once products land it has no further data needs. */}
      {catalogProducts !== null && catalogProducts.length > 0 && (
        <section
          className="rounded-2xl p-6"
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
          }}
          aria-labelledby="simulator-heading"
        >
          <header className="mb-4">
            <h2
              id="simulator-heading"
              className="text-lg font-semibold mb-1"
              style={{ color: colors.ink }}
            >
              See what your money could do
            </h2>
            <p className="text-sm" style={{ color: colors.muted }}>
              Try our series with an example amount. No commitment.
            </p>
          </header>
          <YieldSimulatorWidget products={catalogProducts} fitProfileReady={fitProfileReady} />
        </section>
      )}

      <TrustStrip />
    </div>
  );
};

// ─── Onboarding callout (unchanged from original) ───────────────────
const OnboardingCallout = ({ state }: Readonly<{ state: OnboardingState }>): JSX.Element | null => {
  // An unknown status is rendered by the error panel, never as "Get started".
  if (state.loading || state.error) return null;

  if (!state.case) {
    return (
      <div
        className="rounded-2xl p-6 flex items-center justify-between gap-6 flex-wrap"
        style={{ background: gradients.navy, color: '#FFFFFF', boxShadow: shadows.topbar }}
      >
        <div className="min-w-0">
          <p
            className="text-xs font-bold uppercase tracking-widest mb-2"
            style={{ color: colors.gold }}
          >
            Get started
          </p>
          <h3 className="text-lg font-semibold mb-1">Complete your KYC onboarding</h3>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>
            We need a few details before you can contribute. Save as you go — this takes about 10 minutes.
          </p>
        </div>
        <Link
          to="/client/onboarding"
          className="px-5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap"
          style={{ background: gradients.gold, color: colors.navy, boxShadow: shadows.goldCta }}
        >
          Start onboarding
        </Link>
      </div>
    );
  }

  const c = state.case;
  const tone: BadgeTone = STATE_TONE[c.current_state_slug] ?? 'info';
  // A state this screen has no words for is still not shown by its internal name.
  const label = STATUS_LABEL[c.current_state_slug] ?? 'In progress';
  const isClosed = c.current_state_slug === 'rejected';

  const blurb = (() => {
    if (c.current_state_slug === 'rejected') {
      return 'Your application was not approved. Contact your Account Executive for next steps.';
    }
    if (c.needs_my_action) return 'A step is waiting for you.';
    return 'Our team is reviewing your submission.';
  })();

  return (
    <div
      className="rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-base font-semibold" style={{ color: colors.ink }}>
            Onboarding
          </h3>
          <StatusBadge tone={tone}>{label}</StatusBadge>
        </div>
        <p className="text-sm" style={{ color: colors.muted }}>{blurb}</p>
      </div>
      {!isClosed && (
        <Link
          to="/client/onboarding"
          className="px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap"
          style={{
            background: c.needs_my_action ? gradients.gold : colors.surface,
            color: c.needs_my_action ? colors.navy : colors.inkSoft,
            border: c.needs_my_action ? 'none' : `1px solid ${colors.border}`,
          }}
        >
          {c.needs_my_action ? 'Continue' : 'View status'}
        </Link>
      )}
    </div>
  );
};
